/**
 * CreateSynonymDialog - Form-based dialog for creating Oracle synonyms.
 *
 * Fields: Synonym Name, Schema, Target Schema, Target Object, Public toggle.
 * DDL preview panel shows the generated CREATE [PUBLIC] SYNONYM statement.
 */

import { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useOracleObjectDialogStore } from '../store/oracleObjectDialogStore';
import { generateCreateSynonymDDL } from '../utils/ddlGenerators';
import { useWorkspaceStore } from '../../core/store/workspaceStore';
import { getConnectionById } from '../../../connections/utils/connectionLookup';
import { safeConnectionAlias, safeConnectionServer } from '@/types/safeConnectionDisplay';

export function CreateSynonymDialog() {
  const { openDialog, context, close } = useOracleObjectDialogStore();
  const isOpen = openDialog === 'create-synonym' && !!context;

  const [name, setName] = useState('');
  const [targetSchema, setTargetSchema] = useState('');
  const [targetObject, setTargetObject] = useState('');
  const [isPublic, setIsPublic] = useState(false);

  const ddl = useMemo(() => {
    if (!context) return '';
    return generateCreateSynonymDDL({
      schema: context.schema,
      name: name || 'NEW_SYNONYM',
      targetSchema: targetSchema || context.schema,
      targetObject: targetObject || 'TARGET_OBJECT',
      isPublic,
    });
  }, [context, name, targetSchema, targetObject, isPublic]);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      resetForm();
      close();
    }
  };

  const resetForm = () => {
    setName('');
    setTargetSchema('');
    setTargetObject('');
    setIsPublic(false);
  };

  const handleCopyToEditor = () => {
    if (!context) return;
    openQueryTab(context.connectionId, context.database, ddl, 'New Synonym');
    resetForm();
    close();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[700px]" data-testid="create-synonym-dialog">
        <DialogHeader>
          <DialogTitle>New Synonym</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4">
          {/* Form Fields */}
          <div className="space-y-3">
            <div>
              <Label htmlFor="syn-schema">Schema</Label>
              <Input id="syn-schema" value={context?.schema ?? ''} disabled className="mt-1" />
            </div>
            <div>
              <Label htmlFor="syn-name">Synonym Name</Label>
              <Input id="syn-name" data-testid="syn-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="NEW_SYNONYM" className="mt-1" />
            </div>
            <div>
              <Label htmlFor="syn-target-schema">Target Schema</Label>
              <Input id="syn-target-schema" data-testid="syn-target-schema" value={targetSchema} onChange={(e) => setTargetSchema(e.target.value)} placeholder={context?.schema ?? ''} className="mt-1" />
            </div>
            <div>
              <Label htmlFor="syn-target-object">Target Object</Label>
              <Input id="syn-target-object" data-testid="syn-target-object" value={targetObject} onChange={(e) => setTargetObject(e.target.value)} placeholder="TABLE_OR_VIEW_NAME" className="mt-1" />
            </div>
            <div className="flex items-center gap-2 pt-1">
              <Switch id="syn-public" checked={isPublic} onCheckedChange={setIsPublic} />
              <Label htmlFor="syn-public">Public Synonym</Label>
            </div>
          </div>

          {/* DDL Preview */}
          <div>
            <Label>DDL Preview</Label>
            <pre
              className="mt-1 p-3 bg-muted rounded-md text-xs font-mono whitespace-pre-wrap min-h-[200px] border border-border overflow-auto max-h-[300px]"
              data-testid="syn-ddl-preview"
            >
              {ddl}
            </pre>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>Cancel</Button>
          <Button variant="outline" onClick={handleCopyToEditor} data-testid="syn-copy-to-editor">
            Copy to Query Editor
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function openQueryTab(connectionId: string, database: string, ddl: string, title: string) {
  const { activeConnection, connection } = getConnectionById(connectionId);
  if (!activeConnection || !connection) return;

  const { addTab, getNextQueryNumber } = useWorkspaceStore.getState();
  const queryNumber = getNextQueryNumber();

  addTab({
    title: `${title}-${queryNumber}`,
    type: 'query',
    content: { query: ddl },
    connectionInfo: {
      connectionId,
      connectionName: safeConnectionAlias(connection, 'Unnamed Connection'),
      serverHost: safeConnectionServer(activeConnection.connection),
      database,
    },
  });
}
